import React, { useState, useEffect } from 'react';
import axios from 'axios';

const MarksheetTemplateManager = ({ onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [template, setTemplate] = useState({
    subjects: [
      { name: 'hindi', displayName: 'Hindi' },
      { name: 'english', displayName: 'English' },
      { name: 'mathematics', displayName: 'Mathematics' },
      { name: 'science', displayName: 'Science' },
      { name: 'socialScience', displayName: 'Social Science' },
      { name: 'drawing', displayName: 'Drawing' },
      { name: 'computer', displayName: 'Computer' },
      { name: 'gk', displayName: 'G.K.' },
      { name: 'moralScience', displayName: 'Moral Science' }
    ],
    examStructure: {
      term1: { 
        enabled: true, 
        maxMarks: 10, 
        order: 1, 
        displayName: 'Term 1',
        useSubjectSpecificMarks: false,
        subjectMaxMarks: {}
      },
      term2: { 
        enabled: true, 
        maxMarks: 10, 
        order: 2, 
        displayName: 'Term 2',
        useSubjectSpecificMarks: false,
        subjectMaxMarks: {}
      },
      halfYearly: { 
        enabled: true, 
        maxMarks: 80, 
        order: 3, 
        displayName: 'Half Yearly',
        useSubjectSpecificMarks: false,
        subjectMaxMarks: {}
      },
      term3: { 
        enabled: true, 
        maxMarks: 10, 
        order: 4, 
        displayName: 'Term 3',
        useSubjectSpecificMarks: false,
        subjectMaxMarks: {}
      },
      project: { 
        enabled: true, 
        maxMarks: 10, 
        order: 5, 
        displayName: 'Project Work',
        useSubjectSpecificMarks: false,
        subjectMaxMarks: {}
      },
      annualExam: { 
        enabled: true, 
        maxMarks: 80, 
        order: 6, 
        displayName: 'Annual Exam',
        useSubjectSpecificMarks: false,
        subjectMaxMarks: {}
      }
    },
    additionalFields: []
  });
  useEffect(() => {
    fetchTemplates();
    fetchClasses();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/marksheet-templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get('/api/classes');
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const loadTemplate = async (className) => {
    try {
      const response = await axios.get(`/api/marksheet-template/${className}`);
      // Ensure all exam structures have the new fields
      const loadedTemplate = response.data;
      Object.keys(loadedTemplate.examStructure).forEach(examKey => {
        if (!loadedTemplate.examStructure[examKey].hasOwnProperty('useSubjectSpecificMarks')) {
          loadedTemplate.examStructure[examKey].useSubjectSpecificMarks = false;
        }
        if (!loadedTemplate.examStructure[examKey].hasOwnProperty('subjectMaxMarks')) {
          loadedTemplate.examStructure[examKey].subjectMaxMarks = {};
        }
      });
      setTemplate(loadedTemplate);
      setSelectedClass(className);
    } catch (error) {
      // Template doesn't exist, use default
      setSelectedClass(className);
    }
  };

  const saveTemplate = async () => {
    if (!selectedClass) {
      alert('Please select a class');
      return;
    }

    try {
      console.log('Saving template:', {
        className: selectedClass,
        ...template,
        createdBy: 'Admin Level 1'
      });
      
      const response = await axios.put(`/api/marksheet-template/${selectedClass}`, {
        className: selectedClass,
        ...template,
        createdBy: 'Admin Level 1'
      });
      
      console.log('Template saved successfully:', response.data);
      alert('Template saved successfully!');
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      console.error('Error response:', error.response?.data);
      alert(`Error saving template: ${error.response?.data?.error || error.message}`);
    }
  };

  const addSubject = () => {
    setTemplate(prev => ({
      ...prev,
      subjects: [...prev.subjects, { name: '', displayName: '' }]
    }));
  };

  const updateSubject = (index, field, value) => {
    setTemplate(prev => ({
      ...prev,
      subjects: prev.subjects.map((subject, i) => 
        i === index ? { ...subject, [field]: value } : subject
      )
    }));
  };

  const removeSubject = (index) => {
    setTemplate(prev => ({
      ...prev,
      subjects: prev.subjects.filter((_, i) => i !== index)
    }));
  };

  const addAdditionalField = () => {
    setTemplate(prev => ({
      ...prev,
      additionalFields: [...(prev.additionalFields || []), { name: '', displayName: '', type: 'text', category: 'coCurricular' }]
    }));
  };

  const updateAdditionalField = (index, field, value) => {
    setTemplate(prev => ({
      ...prev,
      additionalFields: (prev.additionalFields || []).map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeAdditionalField = (index) => {
    setTemplate(prev => ({
      ...prev,
      additionalFields: (prev.additionalFields || []).filter((_, i) => i !== index)
    }));
  };

  const updateExamStructure = (exam, field, value) => {
    setTemplate(prev => ({
      ...prev,
      examStructure: {
        ...prev.examStructure,
        [exam]: {
          ...prev.examStructure[exam],
          [field]: value
        }
      }
    }));
  };

  const addCustomExam = () => {
    const examName = prompt('Enter exam name (e.g., "Monthly Test", "Unit Test"):');
    if (examName && examName.trim()) {
      const examKey = examName.toLowerCase().replace(/\s+/g, '');
      const maxOrder = Math.max(...Object.values(template.examStructure).map(e => e.order || 0));
      setTemplate(prev => ({
        ...prev,
        examStructure: {
          ...prev.examStructure,
          [examKey]: {
            enabled: true,
            maxMarks: 10,
            displayName: examName.trim(),
            order: maxOrder + 1,
            useSubjectSpecificMarks: false,
            subjectMaxMarks: {}
          }
        }
      }));
    }
  };

  const updateExamOrder = (examKey, newOrder) => {
    setTemplate(prev => ({
      ...prev,
      examStructure: {
        ...prev.examStructure,
        [examKey]: {
          ...prev.examStructure[examKey],
          order: parseInt(newOrder)
        }
      }
    }));
  };

  const removeCustomExam = (examKey) => {
    const defaultExams = ['term1', 'term2', 'halfYearly', 'term3', 'project', 'annualExam'];
    if (defaultExams.includes(examKey)) {
      alert('Cannot remove default exam types');
      return;
    }
    
    if (window.confirm('Are you sure you want to remove this exam type?')) {
      setTemplate(prev => {
        const newExamStructure = { ...prev.examStructure };
        delete newExamStructure[examKey];
        return {
          ...prev,
          examStructure: newExamStructure
        };
      });
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', zIndex: 5000, overflow: 'auto', padding: '20px' }}>
      <div style={{ background: 'white', borderRadius: '15px', width: '100%', maxWidth: '1000px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }}>
        
        <div style={{ padding: '20px', borderBottom: '2px solid #f59e0b', background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, color: '#92400e', fontSize: '24px', fontWeight: 'bold' }}>📋 Marksheet Template Manager</h2>
            <button onClick={onClose} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '50%', cursor: 'pointer', fontSize: '18px' }}>✕</button>
          </div>
          <p style={{ margin: '5px 0 0 0', color: '#92400e' }}>Admin Level 1 - Define marksheet patterns for all classes</p>
        </div>

        <div style={{ padding: '20px' }}>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Select Class:</label>
            <select 
              value={selectedClass} 
              onChange={(e) => loadTemplate(e.target.value)}
              style={{ width: '200px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              <option value="">Choose a class...</option>
              {classes.map(cls => (
                <option key={cls._id} value={cls.name}>{cls.name}</option>
              ))}
            </select>
          </div>

          {selectedClass && (
            <>
              <div style={{ marginBottom: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '10px' }}>
                <h3 style={{ color: '#1e40af', marginBottom: '15px' }}>📚 Subjects Configuration</h3>
                {template.subjects.map((subject, index) => (
                  <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="Subject key (e.g., hindi)"
                      value={subject.name}
                      onChange={(e) => updateSubject(index, 'name', e.target.value)}
                      style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <input
                      type="text"
                      placeholder="Display name (e.g., Hindi)"
                      value={subject.displayName}
                      onChange={(e) => updateSubject(index, 'displayName', e.target.value)}
                      style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <button 
                      onClick={() => removeSubject(index)}
                      style={{ background: '#dc2626', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button 
                  onClick={addSubject}
                  style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', marginTop: '10px' }}
                >
                  + Add Subject
                </button>
              </div>

              <div style={{ marginBottom: '30px', padding: '20px', background: '#f0f9ff', borderRadius: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ color: '#1e40af', margin: 0 }}>📊 Exam Structure Configuration</h3>
                  <button 
                    onClick={addCustomExam}
                    style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
                  >
                    + Add Custom Exam
                  </button>
                </div>
                <div style={{ marginBottom: '15px', padding: '10px', background: '#fef3c7', borderRadius: '6px', border: '1px solid #f59e0b' }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#92400e' }}>
                    💡 <strong>Subject-Specific Marks:</strong> You can now set different maximum marks for each subject in each exam. 
                    Perfect for Nursery, LKG, UKG where subjects may have different assessment patterns.
                  </p>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '15px' }}>
                  {Object.entries(template.examStructure)
                    .sort(([,a], [,b]) => (a.order || 0) - (b.order || 0))
                    .map(([exam, config]) => {
                    const defaultExams = ['term1', 'term2', 'halfYearly', 'term3', 'project', 'annualExam'];
                    const isCustom = !defaultExams.includes(exam);
                    return (
                      <div key={exam} style={{ padding: '15px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', position: 'relative' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <h4 style={{ margin: 0, color: '#374151' }}>
                            {config.displayName || exam.replace(/([A-Z])/g, ' $1')}
                          </h4>
                          {isCustom && (
                            <button 
                              onClick={() => removeCustomExam(exam)}
                              style={{ background: '#dc2626', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <input
                              type="checkbox"
                              checked={config.enabled}
                              onChange={(e) => updateExamStructure(exam, 'enabled', e.target.checked)}
                            />
                            Enabled
                          </label>
                          <div style={{ marginLeft: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>Order:</label>
                            <input
                              type="number"
                              min="1"
                              value={config.order || 1}
                              onChange={(e) => updateExamOrder(exam, e.target.value)}
                              style={{ width: '50px', padding: '4px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px' }}
                            />
                          </div>
                        </div>
                        
                        {config.enabled && (
                          <div>
                            <div style={{ marginBottom: '10px', padding: '8px', background: '#f8fafc', borderRadius: '4px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <input
                                  type="checkbox"
                                  checked={config.useSubjectSpecificMarks || false}
                                  onChange={(e) => updateExamStructure(exam, 'useSubjectSpecificMarks', e.target.checked)}
                                />
                                <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e40af' }}>Use different max marks for each subject</span>
                              </label>
                              {!config.useSubjectSpecificMarks && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <label style={{ fontSize: '12px', color: '#6b7280' }}>Same max marks for all subjects:</label>
                                  <input
                                    type="number"
                                    value={config.maxMarks || 10}
                                    onChange={(e) => updateExamStructure(exam, 'maxMarks', parseInt(e.target.value))}
                                    style={{ width: '60px', padding: '4px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px' }}
                                  />
                                </div>
                              )}
                            </div>
                            
                            {config.useSubjectSpecificMarks && (
                              <div style={{ marginTop: '10px' }}>
                                <h5 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#374151' }}>Subject-Specific Maximum Marks:</h5>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px' }}>
                                  {template.subjects.map((subject, index) => (
                                    <div key={subject.name} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px', background: '#f9fafb', borderRadius: '4px' }}>
                                      <label style={{ fontSize: '11px', color: '#374151', minWidth: '60px' }}>{subject.displayName}:</label>
                                      <input
                                        type="number"
                                        min="1"
                                        value={config.subjectMaxMarks?.[subject.name] || config.maxMarks || 10}
                                        onChange={(e) => {
                                          const newSubjectMaxMarks = { ...config.subjectMaxMarks };
                                          newSubjectMaxMarks[subject.name] = parseInt(e.target.value) || 10;
                                          updateExamStructure(exam, 'subjectMaxMarks', newSubjectMaxMarks);
                                        }}
                                        style={{ width: '50px', padding: '3px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px' }}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ marginBottom: '30px', padding: '20px', background: '#fef7ff', borderRadius: '10px' }}>
                <h3 style={{ color: '#7c3aed', marginBottom: '15px' }}>🎨 Additional Fields (Co-curricular & Attendance)</h3>
                {(template.additionalFields || []).map((field, index) => (
                  <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="Field key (e.g., drawing)"
                      value={field.name}
                      onChange={(e) => updateAdditionalField(index, 'name', e.target.value)}
                      style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <input
                      type="text"
                      placeholder="Display name (e.g., Drawing)"
                      value={field.displayName}
                      onChange={(e) => updateAdditionalField(index, 'displayName', e.target.value)}
                      style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <select
                      value={field.type}
                      onChange={(e) => updateAdditionalField(index, 'type', e.target.value)}
                      style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                      <option value="text">Text</option>
                      <option value="grade">Grade</option>
                      <option value="boolean">Yes/No</option>
                    </select>
                    <select
                      value={field.category}
                      onChange={(e) => updateAdditionalField(index, 'category', e.target.value)}
                      style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                      <option value="coCurricular">Co-curricular</option>
                      <option value="attendance">Attendance</option>
                    </select>
                    <button 
                      onClick={() => removeAdditionalField(index)}
                      style={{ background: '#dc2626', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button 
                  onClick={addAdditionalField}
                  style={{ background: '#7c3aed', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', marginTop: '10px' }}
                >
                  + Add Field
                </button>
              </div>

              <div style={{ textAlign: 'center' }}>
                <button 
                  onClick={saveTemplate}
                  style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', border: 'none', padding: '12px 30px', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' }}
                >
                  💾 Save Template for {selectedClass}
                </button>
              </div>
            </>
          )}

          {templates.length > 0 && (
            <div style={{ marginTop: '30px', padding: '20px', background: '#f3f4f6', borderRadius: '10px' }}>
              <h3 style={{ color: '#374151', marginBottom: '15px' }}>📋 Existing Templates</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                {templates.map(tmpl => (
                  <div key={tmpl.className} style={{ padding: '10px', background: 'white', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                    <strong>{tmpl.className}</strong>
                    <p style={{ margin: '5px 0', fontSize: '12px', color: '#6b7280' }}>
                      {tmpl.subjects.length} subjects
                    </p>
                    <button 
                      onClick={() => loadTemplate(tmpl.className)}
                      style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                    >
                      Edit
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarksheetTemplateManager;